import {
  useVehicleBrandsByYear,
  useVehicleModelsByYearAndBrand,
} from "Store/findVehicleType/hooks";
import {
  resetSelectedModel,
  resetSelectedModelConfiguration,
  resetSelectedYear,
} from "../utils";
import {
  handleVehicleModelTypeChange,
  modelTypeOptions,
  namedValueToOption,
} from "./utils";

import InlineAlert from "@storeblocks/inline-alert";
import { StyledAlert } from "App/global-style";
import { MotorcycleProductIntroFormValues } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Motorcycle/motorcycleInterface";
import { VEHICLE_TYPE } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/vehicleConstants";
import { AccessibleCallUsMessage } from "Components/Form/AccessibleCallUsMessage";
import { AlertType } from "Components/Form/Alert";
import { Select } from "Components/Form/Select";
import { useTexts } from "Components/TextContext";
import { getValidRegistrationYearOptions } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/vehicleUtils";
import { isValidEntity } from "Utils/apiUtils";
import { addDefaultSelectOption } from "Utils/formUtils";
import { getError } from "Utils/productUtils";
import { FormikProps } from "formik";
import React from "react";
import { CarProductIntroFormValues } from "../interfaces";
import { FindVehicleTypeTexts } from "./findVehicleTypeInterface";

interface Props {
  readonly vehicleType: VEHICLE_TYPE;
  readonly vehicleFetched: boolean;
  readonly formProps: FormikProps<
    CarProductIntroFormValues | MotorcycleProductIntroFormValues
  >;
  texts: FindVehicleTypeTexts;
}

export const FindVehicleType = ({
  vehicleType,
  vehicleFetched,
  formProps,
  texts,
}: Props): React.ReactElement => {
  const { getText } = useTexts();

  const brands = useVehicleBrandsByYear(vehicleType, formProps.values.year);
  const models = useVehicleModelsByYearAndBrand(
    vehicleType,
    formProps.values.year,
    formProps.values.brand,
  );

  const brandsFetched = isValidEntity(brands);
  const modelsFetched = isValidEntity(models);

  const yearOptions = addDefaultSelectOption(
    texts.yearPlaceholder,
    getValidRegistrationYearOptions(vehicleType),
  );
  const brandOptions = addDefaultSelectOption(
    texts.brandPlaceholder,
    brands.current?.map(namedValueToOption) ?? [],
  );
  const modelOptions = addDefaultSelectOption(
    texts.modelPlaceholder,
    models.current?.map(namedValueToOption) ?? [],
  );

  return (
    <>
      <Select
        isLoading={brands.isLoading}
        name="year"
        id="year"
        label={texts.yearLabel}
        hint={texts.yearHint ?? ""}
        options={yearOptions}
        error={getError("year", formProps)}
        onChange={() => resetSelectedYear(formProps)}
        disabled={vehicleFetched}
      />
      <Select
        isLoading={models.isLoading}
        name="brand"
        id="brand"
        label={texts.brandLabel}
        hint={texts.brandHint ?? ""}
        options={brandOptions}
        onChange={() => resetSelectedModel(formProps)}
        error={getError("brand", formProps)}
        disabled={vehicleFetched || !brandsFetched || !!formProps.errors.year}
        large
      />
      {models.error && (
        <StyledAlert
          type="error"
          alertId="brandSearchError"
          alertType={AlertType.SYSTEM_ERROR_ALERT}
          title={texts.brandSearchErrorAlertTitle}
          description={texts.brandSearchErrorAlertDescription}
        >
          <AccessibleCallUsMessage />
        </StyledAlert>
      )}
      <Select
        name="model"
        id="model"
        label={texts.modelLabel}
        hint={texts.modelHint ?? ""}
        options={modelOptions}
        onChange={() => resetSelectedModelConfiguration(formProps)}
        error={getError("model", formProps)}
        disabled={vehicleFetched || !modelsFetched || !!formProps.errors.brand}
        large
      />
      {vehicleType === VEHICLE_TYPE.CAR && (
        <Select
          name="modelType"
          id="modelType"
          label={getText("carform.label.modeltype")}
          options={modelTypeOptions(
            vehicleType,
            texts.modelTypePlaceholder ?? "",
            models,
            formProps,
          )}
          onChange={(event) =>
            handleVehicleModelTypeChange(
              event,
              models.current,
              formProps as FormikProps<CarProductIntroFormValues>,
            )
          }
          error={getError("modelType", formProps)}
          disabled={
            !vehicleFetched && (!modelsFetched || !!formProps.errors.model)
          }
          large
        />
      )}
      <InlineAlert title={texts.brandNotPresentAlertTitle} />
    </>
  );
};
